/**
 * Delivery Date Validator Module
 * Handles validation of delivery dates in cart and checkout
 * Extracted from theme.js for better maintainability
 */

(function() {
    'use strict';

    // Wait for halo object to be available
    if (typeof window.halo === 'undefined') {
        // Only log in development environments
        if (window.location.hostname.includes('dev') || window.location.hostname.includes('stage') || window.location.hostname.includes('localhost')) {
            console.error('deliveryDateValidator: halo object not found. Make sure theme.js loads first.');
        }
        return;
    }

    // ============================================================================
    // CONSTANTS
    // ============================================================================
    const CONSTANTS = {
        SELECTORS: {
            // Cart containers
            QUICK_CART: '#halo-cart-sidebar',
            MAIN_CART_ITEMS: '#main-cart-items',
            CART_ITEMS_WRAPPER: '.cart-items-wrapper',
            CART_ITEMS_DATA: '[data-cart-items]',

            // Buttons
            QUICK_CART_BUTTON: '#halo-cart-sidebar .btn-checkout, #halo-cart-sidebar .button-checkout, #halo-cart-sidebar [href="/checkout"]',
            MAIN_CART_BUTTON: '#cart-checkout',
            CART_SIDEBAR_CHECKOUT: '#cart-sidebar-checkout',

            // Delivery date elements
            DELIVERY_DATE_ALERT: '.delivery-date-alert',
            DELIVERY_DATE_EDIT: '.delivery-date-edit',
            DELIVERY_DATE_ICONS: '.delivery-date-icons',
            DELIVERY_DATE_POPOVER: '.delivery-date-popover',

            // Calendar
            CALENDAR_CONTAINER: '#ff-product-page-options-modal',
            DELIVERY_DATE_INPUT: 'input[name="delivery_date_input"]',

            // Modal
            MODAL_ID: 'delivery-date-edit-modal-quickcart',
            MODAL_CLOSE: '.delivery-date-modal-close',
            MODAL_OVERLAY: '.delivery-date-modal-overlay',
            MODAL_CANCEL: '.delivery-date-modal-cancel',
            MODAL_SAVE: '.delivery-date-modal-save',

            // Other
            CART_ITEM: '.cart-item',
            TEMPLATE_CART: '.template-cart'
        },

        TIMEOUTS: {
            POPOVER_HIDE: 100,
            OBSERVER_DEBOUNCE: 300,
            MODAL_ANIMATION: 300,
            CALENDAR_INIT: 150,
            BUTTON_RECONNECT: 100,
            POLLING_INTERVAL: 500,
            POLLING_START_DELAY: 1000,
            MODAL_OPEN_DELAY: 10,
            CLICK_OUTSIDE_DELAY: 100,
            DATE_UPDATE_SUCCESS: 500,
            VALIDATING_CLEAR: 0
        },

        CACHE_TTL: 21600000, // 6 hours (6 * 60 * 60 * 1000)
        MIN_DAYS_AHEAD: 3,

        CSS_CLASSES: {
            VALIDATING: 'validating',
            DISABLED: 'disabled',
            LOADING: 'loading',
            IS_VISIBLE: 'is-visible',
            IS_OPEN: 'is-open',
            ACTIVE: 'active',
            POPOVER_CLICK: 'delivery-date-popover--click',
            POPOVER_MOUSEENTER: 'delivery-date-popover--mouseenter'
        },

        MESSAGES: {
            DEFAULT_EXPIRED: 'Delivery date has expired. Please select a new date.',
            UNAVAILABLE: 'Product currently unavailable',
            MODAL_TITLE: 'Select Delivery Date',
            BUTTON_UPDATE: 'Update Date',
            BUTTON_CANCEL: 'Cancel',
            BUTTON_UPDATING: 'Updating...',
            UPDATE_FAILED: 'Failed to update delivery date. Please try again.',
            CHECKOUT_DEFAULT: 'Checkout',
            PROCEED_TO_CHECKOUT: 'Proceed To Checkout'
        },

        DATA_ATTRIBUTES: {
            LINE_KEY: 'data-line-key',
            PRODUCT_HANDLE: 'data-product-handle',
            PRODUCT_ID: 'data-product-id',
            VARIANT_ID: 'data-variant-id',
            CURRENT_DATE: 'data-current-date',
            ORIGINAL_TEXT: 'data-original-text',
            DISABLED_BY_VALIDATOR: 'data-disabled-by-validator',
            EXPIRED_MESSAGE: 'data-expired-message',
            HAS_EXPIRED_DATE: 'data-has-expired-date',
            TRIGGER: 'data-trigger'
        }
    };

    // ============================================================================
    // DOM HELPER MODULE
    // ============================================================================
    const DOMHelper = {
        /**
         * Convert data attribute name to dataset property name
         * Example: 'data-original-text' -> 'originalText'
         */
        toDatasetKey: function(dataAttr) {
            return dataAttr
                .replace('data-', '')
                .replace(/-([a-z])/g, (match, letter) => letter.toUpperCase());
        },

        /**
         * Get normalized event target (handles both element and text nodes)
         */
        getTarget: function(event) {
            const target = event.target;
            return target && target.nodeType === Node.ELEMENT_NODE
                ? target
                : target.parentElement;
        },

        /**
         * Detect which cart context an element belongs to
         */
        getCartContext: function(element) {
            const isInQuickCart = element.closest(CONSTANTS.SELECTORS.QUICK_CART) !== null;
            const isInMainCart = element.closest(CONSTANTS.SELECTORS.TEMPLATE_CART) !== null ||
                                element.closest(CONSTANTS.SELECTORS.MAIN_CART_ITEMS) !== null;

            return {
                isInQuickCart,
                isInMainCart,
                location: isInQuickCart ? 'quick-cart' : 'main-cart'
            };
        },

        /**
         * Get both checkout button elements
         */
        getButtonElements: function() {
            return {
                quickCartButton: document.querySelector(CONSTANTS.SELECTORS.QUICK_CART_BUTTON),
                mainCartButton: document.querySelector(CONSTANTS.SELECTORS.MAIN_CART_BUTTON)
            };
        },

        /**
         * Calculate popover position relative to an element
         */
        calculatePopoverPosition: function(triggerElement, popoverElement) {
            const rect = triggerElement.getBoundingClientRect();
            const popoverRect = popoverElement.getBoundingClientRect();

            let left = rect.left + (rect.width / 2) - (popoverRect.width / 2);
            let top = rect.top - popoverRect.height - 8;

            // Keep within viewport bounds
            if (left < 10) left = 10;
            if (left + popoverRect.width > window.innerWidth - 10) {
                left = window.innerWidth - popoverRect.width - 10;
            }

            // If no room above, show below
            if (top < 10) {
                top = rect.bottom + 8;
            }

            return { left, top };
        },

        /**
         * Check if quick cart is currently open
         */
        isQuickCartOpen: function() {
            const quickCart = document.querySelector(CONSTANTS.SELECTORS.QUICK_CART);
            return quickCart && (
                quickCart.classList.contains(CONSTANTS.CSS_CLASSES.IS_OPEN) ||
                quickCart.classList.contains(CONSTANTS.CSS_CLASSES.ACTIVE)
            );
        },

        /**
         * Check if on cart template page
         */
        isOnCartPage: function() {
            return document.body.classList.contains('template-cart');
        }
    };

    // ============================================================================
    // DATE HELPER MODULE
    // ============================================================================
    const DateHelper = {
        /**
         * Format date string to MM/DD/YYYY format (ensures consistent format for API)
         */
        formatDate: function(dateString) {
            const date = new Date(dateString);
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const year = date.getFullYear();
            return `${month}/${day}/${year}`;
        },

        /**
         * Check if a delivery date is expired (within minDays from today)
         */
        isDateExpired: function(dateString, minDays = CONSTANTS.MIN_DAYS_AHEAD) {
            if (!dateString) return false;

            const today = new Date();
            today.setHours(0, 0, 0, 0);

            const minValidDate = new Date(today);
            minValidDate.setDate(minValidDate.getDate() + minDays);

            const itemDate = new Date(dateString);
            itemDate.setHours(0, 0, 0, 0);

            return itemDate <= minValidDate;
        },

        /**
         * Get delivery date from cart item properties
         */
        getDeliveryDate: function(item) {
            return item.properties ? item.properties['Delivery Date'] : null;
        },

        /**
         * Calculate minimum valid date (today + daysAhead)
         * Fallback to today if daysAhead is not provided
         */
        calculateMinValidDate: function(daysAhead = CONSTANTS.MIN_DAYS_AHEAD) {
            const today = new Date();
            today.setHours(0, 0, 0, 0);

            const minValidDate = new Date(today);
            minValidDate.setDate(minValidDate.getDate() + daysAhead);

            return minValidDate;
        },

        /**
         * Check if item has an Event Date (used for bundles)
         */
        hasEventDate: function(item) {
            return item.properties && item.properties['Event Date'];
        }
    };

    // ============================================================================
    // CACHE MANAGER MODULE
    // ============================================================================
    const CacheManager = {
        cache: {},
        timestamps: {},

        /**
         * Get cached validation result if still valid
         */
        get: function(cacheKey) {
            if (this.cache[cacheKey] && this.timestamps[cacheKey]) {
                const age = Date.now() - this.timestamps[cacheKey];
                if (age < CONSTANTS.CACHE_TTL) {
                    return this.cache[cacheKey];
                }
            }
            return null;
        },

        /**
         * Store validation result in cache
         */
        set: function(cacheKey, result) {
            this.cache[cacheKey] = result;
            this.timestamps[cacheKey] = Date.now();
        },

        /**
         * Generate cache key for item validation
         */
        generateKey: function(item, deliveryDate) {
            return `${item.product_id}_${item.variant_id}_${deliveryDate}`;
        },

        /**
         * Clear entire cache
         */
        clear: function() {
            this.cache = {};
            this.timestamps = {};
        }
    };

    // ============================================================================
    // API BB MODULE
    // ============================================================================
    const APIClient = {
        /**
         * Get authentication token for API calls
         * Uses Basic Auth to retrieve JWT bearer token
         */
        getAuthToken: async function() {
            try {
                const base64 = btoa(
                    `${window.username_data_endpoin_user}:${window.password_data_endpoint}`
                );

                const options = {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Basic ${base64}`,
                    },
                };

                const response = await fetch(window.url_data_endpoint_auth, options);

                if (response.status !== 200) {
                    console.error('Failed to get auth token:', response.status);
                    return null;
                }

                const result = await response.json();

                // Handle both response formats
                if (result.jwt) {
                    return result.jwt;
                } else if (result.data && result.data.token) {
                    return result.data.token;
                }

                console.error('Unexpected auth response format:', result);
                return null;
            } catch (error) {
                console.error('Error getting auth token:', error);
                return null;
            }
        },

        /**
         * Validate delivery dates via API endpoint
         * @param {Object} requestPayload - Items to validate with format: { items: { [lineKey]: { product_id, variant_id, date } } }
         * @param {string} token - Bearer token for authentication
         * @returns {Object|null} Validation results or null on error
         */
        validateDates: async function(requestPayload, token) {
            try {
                const url = `${window.product_data_endpoint}/calendar/validate`;
                const response = await fetch(url, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify(requestPayload)
                });

                if (!response.ok) {
                    console.error(`Validation endpoint failed: ${response.status} ${response.statusText}`);
                    return null;
                }

                const responseData = await response.json();
                return responseData.data || {};
            } catch (error) {
                console.error('Error calling validation endpoint:', error);
                return null;
            }
        },

        /**
         * Resolve supply-kit delivery date via API endpoint (FIF-130).
         * Backend computes mainDate-7, shifts Sat/Sun to Friday, and iterates back
         * over weekdays (up to 10 candidates) if the target is blocked.
         * @param {Object} requestPayload - { items: { [key]: { product_id, variant_id?, main_date } } }
         * @param {string} token - Bearer token
         * @returns {Object|null} Resolved dates per item, or null on network/HTTP error.
         */
        resolveSupplyDate: async function(requestPayload, token) {
            try {
                const url = `${window.product_data_endpoint}/calendar/resolve-supply-date`;
                const response = await fetch(url, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify(requestPayload)
                });

                if (!response.ok) {
                    console.error(`Resolve supply date endpoint failed: ${response.status} ${response.statusText}`);
                    return null;
                }

                const responseData = await response.json();
                return responseData.data || {};
            } catch (error) {
                console.error('Error calling resolve-supply-date endpoint:', error);
                return null;
            }
        }
    };

    // ============================================================================
    // VALIDATION SERVICE MODULE
    // ============================================================================
    const ValidationService = {
        /**
         * Check if cart item should be excluded from delivery date validation
         * Excludes: Bundles (Event Date), Gift Cards, Subscriptions (regular and prepaid), Product Options, Processing Fees
         */
        isExcluded: function(item) {
            // Skip bundles (have Event Date instead of Delivery Date)
            if (DateHelper.hasEventDate(item)) {
                return true;
            }

            // Skip gift cards (have recipient email properties)
            if (item.properties && (
                item.properties['Recipient email'] ||
                item.properties['__shopify_send_gift_card_to_recipient']
            )) {
                return true;
            }

            // Skip subscriptions (have selling_plan_allocation - includes regular and prepaid)
            if (item.selling_plan_allocation) {
                return true;
            }

            // Skip Product Options (use String() to ensure type consistency)
            // window.variant_product_options is defined in custom-variables.liquid
            if (window.variant_product_options && String(item.variant_id) === String(window.variant_product_options)) {
                return true;
            }

            // Skip Processing Fees (generated by MWS app)
            if (item.product_type === 'mws_fee_generated') {
                return true;
            }

            return false;
        },

        /**
         * Validate delivery dates via endpoint with caching
         * @param {Object} cart - Shopify cart object
         * @returns {Object|null} Validation results by line key or null on error
         */
        validateViaEndpoint: async function(cart) {
            try {
                // Get authentication token
                const token = await APIClient.getAuthToken();
                if (!token) {
                    console.error('No auth token available for validation');
                    return null;
                }

                // Build request payload and track cached items
                const requestPayload = { items: {} };
                const cachedResults = {};
                let hasItemsToValidate = false;

                cart.items.forEach(item => {
                    // Exclude bundles, gift cards, and subscriptions
                    if (this.isExcluded(item)) {
                        return;
                    }

                    const deliveryDate = DateHelper.getDeliveryDate(item);
                    if (!deliveryDate) {
                        return;
                    }

                    // Check cache first
                    const cacheKey = CacheManager.generateKey(item, deliveryDate);
                    const cachedResult = CacheManager.get(cacheKey);

                    if (cachedResult) {
                        cachedResults[item.key] = cachedResult;
                        return;
                    }

                    // Need to validate this item
                    hasItemsToValidate = true;
                    requestPayload.items[item.key] = {
                        product_id: item.product_id,
                        variant_id: item.variant_id,
                        date: DateHelper.formatDate(deliveryDate) // Ensure MM/DD/YYYY format
                    };
                });

                // If all results are cached, return cached response
                if (!hasItemsToValidate) {
                    return Object.keys(cachedResults).length > 0 ? cachedResults : {};
                }

                // If no items to validate, return empty object
                if (Object.keys(requestPayload.items).length === 0) {
                    return {};
                }

                // Make API request
                const validationResults = await APIClient.validateDates(requestPayload, token);
                if (!validationResults) {
                    return null;
                }

                // Cache the new results
                Object.entries(validationResults).forEach(([lineKey, result]) => {
                    const item = cart.items.find(i => i.key === lineKey);
                    if (item) {
                        const deliveryDate = DateHelper.getDeliveryDate(item);
                        if (deliveryDate) {
                            const cacheKey = CacheManager.generateKey(item, deliveryDate);
                            CacheManager.set(cacheKey, result);
                        }
                    }
                });

                // Merge cached and new results
                return { ...cachedResults, ...validationResults };
            } catch (error) {
                console.error('Error validating delivery dates via endpoint:', error);
                return null;
            }
        },

        /**
         * Fallback validation using date checking (3-day minimum)
         * @param {Object} cart - Shopify cart object
         * @returns {number} Count of items with expired dates
         */
        validateFallback: function(cart) {
            let expiredCount = 0;

            cart.items.forEach(item => {
                // Skip excluded product types
                if (this.isExcluded(item)) {
                    return;
                }

                const deliveryDate = DateHelper.getDeliveryDate(item);
                if (deliveryDate && DateHelper.isDateExpired(deliveryDate)) {
                    expiredCount++;
                }
            });

            return expiredCount;
        }
    };

    // ============================================================================
    // POPOVER MANAGER MODULE
    // ============================================================================
    const PopoverManager = {
        activePopover: null,
        hideTimeout: null,

        /**
         * Get message to display in popover
         */
        getMessage: function(alertElement, lineKey, lastValidationResults, cartContext) {
            let message = CONSTANTS.MESSAGES.DEFAULT_EXPIRED;
            let showRemainingUnits = false;
            let remainingUnits = 0;

            // First, try to get specific reason from endpoint validation results
            if (lastValidationResults && lastValidationResults[lineKey]) {
                const validationResult = lastValidationResults[lineKey];

                /* 
                 * Disabled detailed messages for now

                if (validationResult.available === false && validationResult.reason) {
                    message = validationResult.reason;

                    // If remaining units available, show that information too
                    if (typeof validationResult.remaining_units === 'number') {
                        showRemainingUnits = true;
                        remainingUnits = validationResult.remaining_units;
                    }
                }
                */
            }
            // If no endpoint result, try default sources for the message based on location
            else if (cartContext.isInQuickCart) {
                // Quick cart - First try window.deliveryDateSettings
                if (window.deliveryDateSettings?.quickCart?.expiredMessage) {
                    message = window.deliveryDateSettings.quickCart.expiredMessage;
                }
                // Then try the cart sidebar data attribute
                else {
                    const cartSidebar = document.querySelector(CONSTANTS.SELECTORS.QUICK_CART);
                    if (cartSidebar && cartSidebar.getAttribute(CONSTANTS.DATA_ATTRIBUTES.EXPIRED_MESSAGE)) {
                        message = cartSidebar.getAttribute(CONSTANTS.DATA_ATTRIBUTES.EXPIRED_MESSAGE);
                    }
                }
            } else if (cartContext.isInMainCart) {
                // Main cart
                if (window.deliveryDateSettings?.mainCart?.expiredMessage) {
                    message = window.deliveryDateSettings.mainCart.expiredMessage;
                }
            }

            // Final fallback to element's data attribute
            if (!message && alertElement.getAttribute(CONSTANTS.DATA_ATTRIBUTES.EXPIRED_MESSAGE)) {
                message = alertElement.getAttribute(CONSTANTS.DATA_ATTRIBUTES.EXPIRED_MESSAGE);
            }

            // Add remaining units info to message if available
            if (showRemainingUnits) {
                if (remainingUnits > 0) {
                    message += ` (${remainingUnits} units available for other dates)`;
                } else {
                    message += ` (${CONSTANTS.MESSAGES.UNAVAILABLE})`;
                }
            }

            return message;
        },

        /**
         * Create popover element
         */
        create: function(popoverId, message, triggerType) {
            const popover = document.createElement('div');
            popover.id = popoverId;
            popover.className = `delivery-date-popover delivery-date-popover--${triggerType}`;
            popover.setAttribute(CONSTANTS.DATA_ATTRIBUTES.TRIGGER, triggerType);
            popover.innerHTML = `
                <div class="delivery-date-popover-content">${message}</div>
                ${triggerType === 'click' ? '<button class="delivery-date-popover-close" type="button">×</button>' : ''}
            `;

            return popover;
        },

        /**
         * Position popover relative to trigger element
         */
        position: function(popover, triggerElement) {
            document.body.appendChild(popover);
            const position = DOMHelper.calculatePopoverPosition(triggerElement, popover);
            popover.style.left = position.left + 'px';
            popover.style.top = position.top + 'px';
        },

        /**
         * Setup click-outside handler for click-triggered popovers
         */
        setupClickOutside: function(popover, alertElement) {
            setTimeout(() => {
                const closeOnClickOutside = function(event) {
                    if (!popover.contains(event.target) && !alertElement.contains(event.target)) {
                        popover.remove();
                        document.removeEventListener('click', closeOnClickOutside);
                    }
                };
                document.addEventListener('click', closeOnClickOutside);
                popover.clickOutsideHandler = closeOnClickOutside;
            }, CONSTANTS.TIMEOUTS.CLICK_OUTSIDE_DELAY);
        },

        /**
         * Setup event handlers for popover
         */
        setupEventHandlers: function(popover, triggerType) {
            const self = this;

            if (triggerType === 'mouseenter') {
                popover.addEventListener('mouseenter', function() {
                    if (self.hideTimeout) {
                        clearTimeout(self.hideTimeout);
                        self.hideTimeout = null;
                    }
                });

                popover.addEventListener('mouseleave', function() {
                    self.hideTimeout = setTimeout(() => {
                        if (popover.getAttribute(CONSTANTS.DATA_ATTRIBUTES.TRIGGER) !== 'click') {
                            popover.remove();
                        }
                    }, CONSTANTS.TIMEOUTS.POPOVER_HIDE);
                });
            } else if (triggerType === 'click') {
                const closeButton = popover.querySelector('.delivery-date-popover-close');
                if (closeButton) {
                    closeButton.addEventListener('click', () => popover.remove());
                }
            }
        },

        /**
         * Convert existing hover popover to click popover
         */
        convertToClick: function(popover, alertElement) {
            const self = this;

            if (self.hideTimeout) {
                clearTimeout(self.hideTimeout);
                self.hideTimeout = null;
            }

            popover.className = `delivery-date-popover ${CONSTANTS.CSS_CLASSES.POPOVER_CLICK}`;
            popover.setAttribute(CONSTANTS.DATA_ATTRIBUTES.TRIGGER, 'click');

            let closeButton = popover.querySelector('.delivery-date-popover-close');
            if (!closeButton) {
                const closeBtn = document.createElement('button');
                closeBtn.className = 'delivery-date-popover-close';
                closeBtn.type = 'button';
                closeBtn.textContent = '×';
                popover.appendChild(closeBtn);
                closeButton = closeBtn;
            }

            // Clone to remove old event listeners
            const newPopover = popover.cloneNode(true);
            popover.parentNode.replaceChild(newPopover, popover);

            const newCloseButton = newPopover.querySelector('.delivery-date-popover-close');
            if (newCloseButton) {
                newCloseButton.addEventListener('click', () => newPopover.remove());
            }

            this.setupClickOutside(newPopover, alertElement);
        }
    };

    // ============================================================================
    // MODAL MANAGER MODULE
    // ============================================================================
    const ModalManager = {
        /**
         * Create modal element for date picker
         */
        createDatePickerModal: function(productId, variantId, calendarLocation) {
            const modal = document.createElement('div');
            modal.id = CONSTANTS.SELECTORS.MODAL_ID;
            modal.className = 'delivery-date-modal';
            modal.style.zIndex = '11';
            modal.innerHTML = `
                <div class="delivery-date-modal-overlay"></div>
                <div class="delivery-date-modal-content">
                    <div class="delivery-date-modal-header">
                        <h3>${CONSTANTS.MESSAGES.MODAL_TITLE}</h3>
                        <button class="delivery-date-modal-close" aria-label="Close">
                            <svg class="icon icon-close" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z"/>
                            </svg>
                        </button>
                    </div>
                    <div class="delivery-date-modal-body">
                        <div
                            data-ff-product-calendar
                            calendar-location="${calendarLocation}"
                            data-product-id="${productId}"
                            data-variant-id="${variantId}"
                            data-lazy="true"
                            id="ff-product-page-options-modal">
                        </div>
                    </div>
                    <div class="delivery-date-modal-footer">
                        <button class="button button-secondary delivery-date-modal-cancel">${CONSTANTS.MESSAGES.BUTTON_CANCEL}</button>
                        <button class="button button-primary delivery-date-modal-save">${CONSTANTS.MESSAGES.BUTTON_UPDATE}</button>
                    </div>
                </div>
            `;

            return modal;
        },

        /**
         * Show modal with animation
         */
        show: function(modal) {
            document.body.appendChild(modal);
            modal.style.display = 'flex';
            setTimeout(() => modal.classList.add(CONSTANTS.CSS_CLASSES.IS_VISIBLE), CONSTANTS.TIMEOUTS.MODAL_OPEN_DELAY);
        },

        /**
         * Close modal with animation
         */
        close: function(modal, pollInterval) {
            if (pollInterval) {
                clearInterval(pollInterval);
            }
            modal.classList.remove(CONSTANTS.CSS_CLASSES.IS_VISIBLE);
            setTimeout(() => {
                modal.remove();
            }, CONSTANTS.TIMEOUTS.MODAL_ANIMATION);
        },

        /**
         * Initialize calendar in modal
         */
        initializeCalendar: function(calendarLocation, productId, variantId, productHandle) {
            setTimeout(() => {
                if (window.initializeCalendarByVariant) {
                    try {
                        window.initializeCalendarByVariant(calendarLocation, productId, variantId);
                    } catch (error) {
                        console.error('Calendar initialization failed:', error);
                    }
                } else if (window.initializeCalendar) {
                    window.initializeCalendar(productHandle, productId, variantId);
                }
            }, CONSTANTS.TIMEOUTS.CALENDAR_INIT);
        }
    };

    // ============================================================================
    // BUTTON CONTROLLER MODULE
    // ============================================================================
    const ButtonController = {
        /**
         * Set loading state on buttons
         */
        setLoadingState: function(quickCartButton, mainCartButton, isLoading) {
            const action = isLoading ? 'add' : 'remove';

            if (quickCartButton) {
                quickCartButton.classList[action](CONSTANTS.CSS_CLASSES.VALIDATING);
                quickCartButton.disabled = isLoading;
            }
            if (mainCartButton) {
                mainCartButton.classList[action](CONSTANTS.CSS_CLASSES.VALIDATING);
                mainCartButton.disabled = isLoading;
            }
        },

        /**
         * Store original button text if not already stored
         */
        storeOriginalText: function(quickCartButton, mainCartButton) {
            const originalTextAttr = DOMHelper.toDatasetKey(CONSTANTS.DATA_ATTRIBUTES.ORIGINAL_TEXT);

            if (quickCartButton && !quickCartButton.dataset[originalTextAttr]) {
                quickCartButton.dataset[originalTextAttr] =
                    quickCartButton.textContent || CONSTANTS.MESSAGES.CHECKOUT_DEFAULT;
            }
            if (mainCartButton && !mainCartButton.dataset[originalTextAttr]) {
                mainCartButton.dataset[originalTextAttr] =
                    mainCartButton.textContent || CONSTANTS.MESSAGES.PROCEED_TO_CHECKOUT;
            }
        },

        /**
         * Update button state based on validation results
         */
        updateButtonState: function(button, unavailableCount, isQuickCart) {
            const originalTextAttr = DOMHelper.toDatasetKey(CONSTANTS.DATA_ATTRIBUTES.ORIGINAL_TEXT);
            const disabledByValidatorAttr = DOMHelper.toDatasetKey(CONSTANTS.DATA_ATTRIBUTES.DISABLED_BY_VALIDATOR);

            if (unavailableCount > 0) {
                // Disable button if there are unavailable items
                const settings = window.deliveryDateSettings?.[isQuickCart ? 'quickCart' : 'mainCart'];
                let buttonText = isQuickCart
                    ? (settings?.reviewText || 'Review Alerts')
                    : (settings?.reviewText || 'Review Expired Products');

                if (unavailableCount === 1 && settings) {
                    const plural = settings.plural || (isQuickCart ? 'Alerts' : 'Products');
                    const singular = settings.singular || (isQuickCart ? 'Alert' : 'Product');
                    buttonText = buttonText.replace(plural, singular);
                }

                button.textContent = buttonText;
                button.disabled = true;
                button.classList.add(CONSTANTS.CSS_CLASSES.DISABLED);
                button.dataset[disabledByValidatorAttr] = 'true';

                button.onclick = (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    return false;
                };
            } else {
                // Enable button if all items are available
                button.textContent = button.dataset[originalTextAttr] ||
                    (isQuickCart ? CONSTANTS.MESSAGES.CHECKOUT_DEFAULT : CONSTANTS.MESSAGES.PROCEED_TO_CHECKOUT);
                button.disabled = false;
                button.classList.remove(CONSTANTS.CSS_CLASSES.DISABLED);
                delete button.dataset[disabledByValidatorAttr];

                button.onclick = () => {
                    window.location = '/checkout';
                };
            }
        }
    };

    // ============================================================================
    // ALERT ICON MANAGER MODULE
    // ============================================================================
    const AlertIconManager = {
        /**
         * Update alert icons for all cart items based on validation results
         */
        updateAll: function(cart, validationResults) {
            if (!cart || !cart.items) return;

            cart.items.forEach(item => {
                this.updateForItem(item, validationResults);
            });
        },

        /**
         * Update alert icon for a single cart item
         */
        updateForItem: function(item, validationResults) {
            // Find the icon container for this line item
            const iconContainer = document.querySelector(
                `${CONSTANTS.SELECTORS.DELIVERY_DATE_ICONS}[${CONSTANTS.DATA_ATTRIBUTES.LINE_KEY}="${item.key}"]`
            );
            if (!iconContainer) return;

            // Determine if alert should be shown
            const shouldShowAlert = this.shouldShowAlert(item, validationResults);

            // Find or create alert icon
            let alertIcon = iconContainer.querySelector(CONSTANTS.SELECTORS.DELIVERY_DATE_ALERT);

            if (shouldShowAlert) {
                this.showAlert(iconContainer, alertIcon, item.key);
            } else {
                this.hideAlert(iconContainer, alertIcon);
            }
        },

        /**
         * Determine if alert should be shown for an item
         */
        shouldShowAlert: function(item, validationResults) {
            // Check if this item has validation results
            const hasValidationResult = validationResults && validationResults[item.key];
            const isUnavailable = hasValidationResult && validationResults[item.key].available === false;

            // If validation results exist, use them
            if (hasValidationResult) {
                return isUnavailable;
            }

            // Otherwise, fall back to date checking
            if (!ValidationService.isExcluded(item)) {
                const deliveryDate = DateHelper.getDeliveryDate(item);
                if (deliveryDate) {
                    return DateHelper.isDateExpired(deliveryDate);
                }
            }

            return false;
        },

        /**
         * Show alert icon
         */
        showAlert: function(iconContainer, alertIcon, lineKey) {
            if (!alertIcon) {
                // Create alert icon if it doesn't exist
                alertIcon = document.createElement('span');
                alertIcon.className = CONSTANTS.SELECTORS.DELIVERY_DATE_ALERT.substring(1);
                alertIcon.setAttribute(CONSTANTS.DATA_ATTRIBUTES.LINE_KEY, lineKey);
                alertIcon.innerHTML = `
                    <svg class="icon-warning" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 21h22L12 2 1 21z" fill="currentColor"/>
                        <circle cx="12" cy="17" r="1.5" fill="white"/>
                        <rect x="10.5" y="10" width="3" height="5" rx="1.5" fill="white"/>
                    </svg>
                `;

                // Insert before the edit icon
                const editIcon = iconContainer.querySelector(CONSTANTS.SELECTORS.DELIVERY_DATE_EDIT);
                if (editIcon) {
                    iconContainer.insertBefore(alertIcon, editIcon);
                } else {
                    iconContainer.appendChild(alertIcon);
                }
            }

            alertIcon.style.display = '';
            iconContainer.setAttribute(CONSTANTS.DATA_ATTRIBUTES.HAS_EXPIRED_DATE, 'true');
        },

        /**
         * Hide alert icon
         */
        hideAlert: function(iconContainer, alertIcon) {
            if (alertIcon) {
                alertIcon.style.display = 'none';
            }
            iconContainer.removeAttribute(CONSTANTS.DATA_ATTRIBUTES.HAS_EXPIRED_DATE);
        }
    };

    // Expose APIClient and DateHelper for reuse (FIF-130 supply-kit delivery-date resolution, etc.)
    window.halo.apiClient = APIClient;
    window.halo.dateHelper = DateHelper;

    // ============================================================================
    // DELIVERY DATE VALIDATOR MODULE
    // ============================================================================
    // Attach deliveryDateValidator to existing halo object
    window.halo.deliveryDateValidator = {
        setupEventListeners: function() {
            const self = this;

            // Handle edit icon clicks
            document.addEventListener('click', function(e) {
                const target = DOMHelper.getTarget(e);
                if (target && target.closest) {
                    const editIcon = target.closest(CONSTANTS.SELECTORS.DELIVERY_DATE_EDIT);
                    if (editIcon) {
                        e.preventDefault();
                        e.stopPropagation();
                        self.openDateEditModal(editIcon);
                    }
                }
            });

            // Handle alert icon clicks
            document.addEventListener('click', function(e) {
                const target = DOMHelper.getTarget(e);
                if (target && target.closest) {
                    const alertIcon = target.closest(CONSTANTS.SELECTORS.DELIVERY_DATE_ALERT);
                    if (alertIcon) {
                        e.preventDefault();
                        e.stopPropagation();
                        self.showExpiredDatePopover(alertIcon, 'click');
                    }
                }
            });

            // Handle alert icon mouseenter
            document.addEventListener('mouseenter', function(e) {
                const target = DOMHelper.getTarget(e);
                if (target && target.closest) {
                    const alertIcon = target.closest(CONSTANTS.SELECTORS.DELIVERY_DATE_ALERT);
                    if (alertIcon) {
                        if (self.popoverHideTimeout) {
                            clearTimeout(self.popoverHideTimeout);
                            self.popoverHideTimeout = null;
                        }
                        self.showExpiredDatePopover(alertIcon, 'mouseenter');
                    }
                }
            }, true);

            // Handle alert icon mouseleave
            document.addEventListener('mouseleave', function(e) {
                const target = DOMHelper.getTarget(e);
                if (target && target.closest) {
                    const alertIcon = target.closest(CONSTANTS.SELECTORS.DELIVERY_DATE_ALERT);
                    if (alertIcon) {
                        self.popoverHideTimeout = setTimeout(() => {
                            const popover = document.querySelector(`.${CONSTANTS.CSS_CLASSES.POPOVER_MOUSEENTER}`);
                            if (popover && !popover.matches(':hover')) {
                                popover.remove();
                            }
                        }, CONSTANTS.TIMEOUTS.POPOVER_HIDE);
                    }
                }
            }, true);
        },

        showExpiredDatePopover: function(alertElement, triggerType = 'click') {
            // Detect cart context
            const cartContext = DOMHelper.getCartContext(alertElement);

            // Get line key
            const parentElement = alertElement.closest(`[${CONSTANTS.DATA_ATTRIBUTES.LINE_KEY}]`);
            const lineKey = parentElement ? parentElement.getAttribute(CONSTANTS.DATA_ATTRIBUTES.LINE_KEY) : 'default';

            // Get message using PopoverManager
            const message = PopoverManager.getMessage(alertElement, lineKey, this.lastValidationResults, cartContext);

            const popoverId = `popover-${lineKey}`;
            let popover = document.getElementById(popoverId);

            // If popover exists and we're clicking it, convert to click mode
            if (popover && triggerType === 'click') {
                PopoverManager.convertToClick(popover, alertElement);
                return;
            }

            // If popover already exists (hover mode), don't recreate
            if (popover) {
                return;
            }

            // Create new popover
            popover = PopoverManager.create(popoverId, message, triggerType);

            // Setup event handlers
            PopoverManager.setupEventHandlers(popover, triggerType);

            // Position and add to DOM
            PopoverManager.position(popover, alertElement);

            // Setup click outside handler for click-triggered popovers
            if (triggerType === 'click') {
                PopoverManager.setupClickOutside(popover, alertElement);
            }
        },

        openDateEditModal: function(editIcon) {
            const lineKey = editIcon.getAttribute(CONSTANTS.DATA_ATTRIBUTES.LINE_KEY);
            const currentDate = editIcon.getAttribute(CONSTANTS.DATA_ATTRIBUTES.CURRENT_DATE);
            const productHandle = editIcon.getAttribute(CONSTANTS.DATA_ATTRIBUTES.PRODUCT_HANDLE);
            const productId = editIcon.getAttribute(CONSTANTS.DATA_ATTRIBUTES.PRODUCT_ID);
            const variantId = editIcon.getAttribute(CONSTANTS.DATA_ATTRIBUTES.VARIANT_ID);

            this.showDatePickerModal(lineKey, currentDate, productHandle, productId, variantId);
        },

        showDatePickerModal: function(lineKey, currentDate, productHandle, productId, variantId) {
            const isInQuickCart = DOMHelper.isQuickCartOpen();
            const calendarLocation = isInQuickCart ? 'quick-cart' : 'main-cart';

            // Remove existing modal if present
            const existingModal = document.querySelector(`#${CONSTANTS.SELECTORS.MODAL_ID}`);
            if (existingModal) {
                existingModal.remove();
            }

            // Create modal using ModalManager
            const modal = ModalManager.createDatePickerModal(productId, variantId, calendarLocation);

            // Show modal with animation
            ModalManager.show(modal);

            // Setup event handlers
            this.setupModalEventHandlers(modal, lineKey, productHandle);

            // Initialize calendar
            ModalManager.initializeCalendar(calendarLocation, productId, variantId, productHandle);
        },

        setupModalEventHandlers: function(modal, lineKey, productHandle) {
            const self = this;
            let selectedDate = null;
            let pollInterval;

            const closeButton = modal.querySelector(CONSTANTS.SELECTORS.MODAL_CLOSE);
            const overlay = modal.querySelector(CONSTANTS.SELECTORS.MODAL_OVERLAY);
            const cancelButton = modal.querySelector(CONSTANTS.SELECTORS.MODAL_CANCEL);
            const saveButton = modal.querySelector(CONSTANTS.SELECTORS.MODAL_SAVE);

            const closeModal = () => {
                ModalManager.close(modal, pollInterval);
            };

            if (closeButton) {
                closeButton.onclick = closeModal;
            }

            if (overlay) {
                overlay.onclick = closeModal;
            }

            if (cancelButton) {
                cancelButton.onclick = closeModal;
            }

            const checkForSelectedDate = () => {
                const hiddenInput = modal.querySelector(CONSTANTS.SELECTORS.DELIVERY_DATE_INPUT);
                if (hiddenInput && hiddenInput.value.trim()) {
                    selectedDate = hiddenInput.value;
                    if (saveButton) {
                        saveButton.disabled = false;
                        saveButton.classList.remove(CONSTANTS.CSS_CLASSES.DISABLED);
                    }
                    return true;
                } else {
                    selectedDate = null;
                    if (saveButton) {
                        saveButton.disabled = true;
                        saveButton.classList.add(CONSTANTS.CSS_CLASSES.DISABLED);
                    }
                    return false;
                }
            };

            if (saveButton) {
                saveButton.disabled = true;
                saveButton.classList.add(CONSTANTS.CSS_CLASSES.DISABLED);

                saveButton.onclick = () => {
                    const hiddenInput = modal.querySelector(CONSTANTS.SELECTORS.DELIVERY_DATE_INPUT);
                    const dateToUse = (hiddenInput && hiddenInput.value) ? hiddenInput.value : selectedDate;

                    if (dateToUse) {
                        const originalText = saveButton.textContent;
                        saveButton.textContent = CONSTANTS.MESSAGES.BUTTON_UPDATING;
                        saveButton.disabled = true;
                        saveButton.classList.add(CONSTANTS.CSS_CLASSES.LOADING);

                        const handleSuccess = () => {
                            setTimeout(() => {
                                saveButton.textContent = originalText;
                                saveButton.disabled = false;
                                saveButton.classList.remove(CONSTANTS.CSS_CLASSES.LOADING);
                                closeModal();
                                Shopify.fetchCart()
                                    .then(cart => {
                                        self.updateCheckoutButton(cart);
                                    })
                                    .catch(error => console.error('Error updating checkout button after date change:', error));
                            }, CONSTANTS.TIMEOUTS.DATE_UPDATE_SUCCESS);
                        };

                        const handleError = () => {
                            saveButton.textContent = originalText;
                            saveButton.disabled = false;
                            saveButton.classList.remove(CONSTANTS.CSS_CLASSES.LOADING);
                        };

                        self.updateCartItemDate(lineKey, dateToUse, productHandle, handleSuccess, handleError);
                    }
                };
            }

            const startPolling = () => {
                pollInterval = setInterval(() => {
                    if (checkForSelectedDate()) {
                    }
                }, CONSTANTS.TIMEOUTS.POLLING_INTERVAL);
            };

            setTimeout(startPolling, CONSTANTS.TIMEOUTS.POLLING_START_DELAY);
        },

        updateCartItemDate: function(lineKey, newDate, productHandle, successCallback, errorCallback) {
            const formattedDate = DateHelper.formatDate(newDate);

            Shopify.fetchCart()
                .then(cart => {
                    const currentItem = cart.items.find(item => item.key === lineKey);

                    if (!currentItem) {
                        throw new Error(`Cart item not found for line key: ${lineKey}`);
                    }

                    // Find all items with the same _associativeID for bulk update
                    const associativeID = currentItem.properties._associativeID;
                    const itemsToUpdate = [];

                    if (associativeID) {
                        // Find all items with matching _associativeID (including the current item)
                        const associatedItems = cart.items.filter(item =>
                            item.properties && item.properties._associativeID === associativeID
                        );
                        itemsToUpdate.push(...associatedItems);
                    } else {
                        // If no associativeID, only update the current item
                        itemsToUpdate.push(currentItem);
                    }

                    // Update items sequentially to avoid race conditions
                    const updateSequentially = async () => {
                        for (let i = 0; i < itemsToUpdate.length; i++) {
                            const item = itemsToUpdate[i];

                            const preservedProperties = { ...item.properties };
                            preservedProperties['Delivery Date'] = formattedDate;

                            await new Promise((resolve, reject) => {
                                Shopify.changeLineItem(item.key, item.quantity, {
                                    properties: preservedProperties,
                                    forceDirectAPI: true,
                                    success: (updatedCart) => {
                                        resolve(updatedCart);
                                    },
                                    error: (error) => {
                                        console.error(`Failed to update delivery date for: ${item.title}`, error);
                                        reject(error);
                                    }
                                });
                            });
                        }
                    };

                    // Execute updates sequentially
                    return updateSequentially();
                })
                .then(() => {
                    // All items updated successfully
                    const isOnCartPage = DOMHelper.isOnCartPage();

                    if (isOnCartPage) {
                        window.location.reload();
                    } else {
                        Shopify.getCart((cart) => {
                            if (window.halo && window.halo.updateSidebarCart) {
                                window.halo.updateSidebarCart(cart);
                            }
                        });
                    }

                    if (successCallback) successCallback();
                })
                .catch(error => {
                    console.error('Error updating delivery date:', error);
                    if (errorCallback) {
                        errorCallback();
                    } else {
                        alert(CONSTANTS.MESSAGES.UPDATE_FAILED);
                    }
                });
        },


        updateCheckoutButton: async function(cart) {
            const self = this;

            // Prevent re-entry
            if (self._isUpdatingButton) return;
            self._isUpdatingButton = true;

            // Disconnect observers temporarily
            if (self._observers) {
                self._observers.forEach(obs => obs.disconnect());
            }

            // Get button elements
            const { quickCartButton, mainCartButton } = DOMHelper.getButtonElements();

            // Store original button text
            ButtonController.storeOriginalText(quickCartButton, mainCartButton);

            // Set loading state
            ButtonController.setLoadingState(quickCartButton, mainCartButton, true);

            let unavailableCount = 0;
            let validationResults = null;

            try {
                // Try to validate via endpoint
                validationResults = await ValidationService.validateViaEndpoint(cart);

                if (validationResults) {
                    // Count unavailable items AND verify all items with delivery dates are validated
                    const validatedKeys = new Set(Object.keys(validationResults));

                    cart.items.forEach(item => {
                        // Skip excluded items
                        if (ValidationService.isExcluded(item)) {
                            return;
                        }

                        const deliveryDate = DateHelper.getDeliveryDate(item);
                        if (!deliveryDate) {
                            return;
                        }

                        // Check if this item was validated
                        if (validatedKeys.has(item.key)) {
                            // Item was validated, check if available
                            if (validationResults[item.key].available === false) {
                                unavailableCount++;
                            }
                        } else {
                            // Item has delivery date but wasn't validated - treat as unavailable for safety
                            console.warn(`Item ${item.key} has delivery date but no validation result. Treating as unavailable.`);
                            unavailableCount++;
                        }
                    });

                    // Store for use in alert messages
                    this.lastValidationResults = validationResults;
                } else {
                    throw new Error('Endpoint validation failed, using fallback');
                }
            } catch (error) {
                console.warn('Using fallback validation method:', error.message);

                // Fallback validation
                unavailableCount = ValidationService.validateFallback(cart);

                // Secondary check with visible alerts
                const visibleAlerts = document.querySelectorAll(
                    `${CONSTANTS.SELECTORS.DELIVERY_DATE_ALERT}:not([style*="display: none"])`
                );
                if (visibleAlerts.length > unavailableCount) {
                    console.warn('Delivery date alert count mismatch detected. Using visible alert count.');
                    unavailableCount = visibleAlerts.length;
                }
            } finally{
                // ALWAYS clear loading state
                setTimeout(() => {
                    ButtonController.setLoadingState(quickCartButton, mainCartButton, false);
                }, CONSTANTS.TIMEOUTS.VALIDATING_CLEAR);
            }

            // Update alert icons using AlertIconManager
            AlertIconManager.updateAll(cart, validationResults);

            // Update button states
            if (quickCartButton) {
                ButtonController.updateButtonState(quickCartButton, unavailableCount, true);
            }
            if (mainCartButton) {
                ButtonController.updateButtonState(mainCartButton, unavailableCount, false);
            }

            // Reconnect observers
            setTimeout(() => {
                if (self._observers) {
                    self._observers.forEach((obs, index) => {
                        const container = [
                            document.querySelector(CONSTANTS.SELECTORS.QUICK_CART),
                            document.querySelector(CONSTANTS.SELECTORS.CART_ITEMS_WRAPPER),
                            document.querySelector(CONSTANTS.SELECTORS.CART_ITEMS_DATA)
                        ][index];

                        if (container && obs) {
                            obs.observe(container, {
                                childList: true,
                                subtree: true,
                                attributes: true,
                                attributeFilter: ['class', CONSTANTS.DATA_ATTRIBUTES.HAS_EXPIRED_DATE]
                            });
                        }
                    });
                }
                self._isUpdatingButton = false;
            }, CONSTANTS.TIMEOUTS.BUTTON_RECONNECT);
        },

        init: function() {
            this.setupEventListeners();
            this.setupCartObserver();
        },

        setupCartObserver: function() {
            const self = this;

            // Initialize observers array
            self._observers = [];

            // Use a shared timeout for all observers to prevent excessive triggering
            let isUpdating = false;

            const updateCartState = () => {
                if (isUpdating) return;
                isUpdating = true;

                clearTimeout(self.observerTimeout);
                self.observerTimeout = setTimeout(() => {
                    Shopify.fetchCart()
                        .then(cart => {
                            self.updateCheckoutButton(cart);
                            isUpdating = false;
                        })
                        .catch(error => {
                            console.error('Error updating checkout button:', error);
                            isUpdating = false;
                        });
                }, CONSTANTS.TIMEOUTS.OBSERVER_DEBOUNCE);
            };

            const cartContainers = [
                document.querySelector(CONSTANTS.SELECTORS.QUICK_CART),
                document.querySelector(CONSTANTS.SELECTORS.CART_ITEMS_WRAPPER),
                document.querySelector(CONSTANTS.SELECTORS.CART_ITEMS_DATA)
            ];

            cartContainers.forEach(container => {
                if (container) {
                    const observer = new MutationObserver((mutations) => {
                        // Skip if we're currently updating the button
                        if (self._isUpdatingButton) {
                            return;
                        }

                        // Only trigger update if mutations actually affect delivery date related elements
                        const hasRelevantChanges = mutations.some(mutation => {
                            // Ignore changes to checkout buttons (we modify these ourselves)
                            if (mutation.target.id === CONSTANTS.SELECTORS.CART_SIDEBAR_CHECKOUT.replace('#', '') ||
                                mutation.target.id === CONSTANTS.SELECTORS.MAIN_CART_BUTTON.replace('#', '') ||
                                mutation.target.classList?.contains('button-checkout')) {
                                return false;
                            }

                            if (mutation.type === 'childList') {
                                return Array.from(mutation.addedNodes).some(node =>
                                    node.nodeType === Node.ELEMENT_NODE &&
                                    (node.classList?.contains(CONSTANTS.SELECTORS.CART_ITEM.substring(1)) ||
                                     node.querySelector?.(`${CONSTANTS.SELECTORS.DELIVERY_DATE_ALERT}, ${CONSTANTS.SELECTORS.DELIVERY_DATE_EDIT}`))
                                ) || Array.from(mutation.removedNodes).some(node =>
                                    node.nodeType === Node.ELEMENT_NODE &&
                                    (node.classList?.contains(CONSTANTS.SELECTORS.CART_ITEM.substring(1)) ||
                                     node.querySelector?.(`${CONSTANTS.SELECTORS.DELIVERY_DATE_ALERT}, ${CONSTANTS.SELECTORS.DELIVERY_DATE_EDIT}`))
                                );
                            }
                            if (mutation.type === 'attributes') {
                                return mutation.attributeName === CONSTANTS.DATA_ATTRIBUTES.HAS_EXPIRED_DATE ||
                                       mutation.target.classList?.contains(CONSTANTS.SELECTORS.DELIVERY_DATE_ALERT.substring(1));
                            }
                            return false;
                        });

                        if (hasRelevantChanges) {
                            updateCartState();
                        }
                    });

                    observer.observe(container, {
                        childList: true,
                        subtree: true,
                        attributes: true,
                        attributeFilter: ['class', CONSTANTS.DATA_ATTRIBUTES.HAS_EXPIRED_DATE]
                    });

                    // Store observer reference
                    self._observers.push(observer);
                }
            });
        }
    };
})();
